import React, { useState, useRef, useEffect } from 'react';
import { Type, AlignLeft, AlignCenter, AlignRight, RotateCw, Trash2, Palette, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const TextBoxFloatingPanel = ({ textBox, position, zoom = 1, onUpdate, onDelete, onClose }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const panelRef = useRef(null);
  const [panelPosition, setPanelPosition] = useState({ x: 0, y: 0 });

  // Calculate panel position based on text box position
  useEffect(() => {
    if (textBox && position) {
      const textBoxRight = position.x + (textBox.width || 150) * zoom + 10;
      const textBoxTop = position.y;
      
      // Default: position to the right of the text box
      let x = textBoxRight;
      let y = textBoxTop;
      
      // Adjust if panel would go off-screen (assuming canvas width ~1000px visible)
      if (x + 220 > 1000) {
        // Position to the left instead
        x = position.x - 220 - 10;
      }
      
      // Keep within reasonable bounds
      x = Math.max(10, x);
      y = Math.max(10, y);
      
      setPanelPosition({ x, y });
    }
  }, [textBox, position, zoom]);

  if (!textBox) return null;

  const handleChange = (field, value) => {
    onUpdate(textBox.id, { [field]: value });
  };

  return (
    <div
      ref={panelRef}
      className="absolute z-50 bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-lg shadow-xl"
      style={{
        left: panelPosition.x,
        top: panelPosition.y,
        width: '220px',
        maxHeight: isExpanded ? '500px' : '44px',
        overflow: 'hidden',
        transition: 'max-height 0.2s ease-out',
      }}
      onClick={(e) => e.stopPropagation()}
      data-testid="text-box-floating-panel"
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between px-3 py-2 border-b border-slate-700 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="text-sm font-medium text-white flex items-center gap-2">
          <Type className="w-4 h-4 text-yellow-400" />
          Text Properties
        </span>
        <div className="flex items-center gap-1">
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-3 space-y-3 max-h-[450px] overflow-y-auto">
          {/* Text Content */}
          <div className="space-y-1">
            <Label className="text-slate-400 text-xs">Text</Label>
            <textarea
              value={textBox.text || ''}
              onChange={(e) => handleChange('text', e.target.value)}
              className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-sm min-h-[50px] resize-none focus:outline-none focus:ring-1 focus:ring-yellow-400"
              placeholder="Enter text..."
            />
          </div>

          {/* Font Size & Color Row */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-slate-400 text-xs">Size (px)</Label>
              <Input
                type="number"
                value={textBox.font_size || 16}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 16;
                  handleChange('font_size', Math.max(8, Math.min(72, val)));
                }}
                className="bg-slate-800 border-slate-600 text-white text-sm h-8"
                min={8}
                max={72}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-400 text-xs">Color</Label>
              <div className="flex items-center gap-1">
                <input
                  type="color"
                  value={textBox.font_color || '#000000'}
                  onChange={(e) => handleChange('font_color', e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border border-slate-600"
                />
                <Input
                  value={textBox.font_color || '#000000'}
                  onChange={(e) => handleChange('font_color', e.target.value)}
                  className="flex-1 bg-slate-800 border-slate-600 text-white font-mono text-xs h-8"
                />
              </div>
            </div>
          </div>

          {/* Background Color */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-slate-400 text-xs">Background</Label>
              <button
                onClick={() => handleChange('background_color', null)}
                className="text-xs text-slate-500 hover:text-yellow-400"
              >
                Clear
              </button>
            </div>
            <div className="flex items-center gap-1">
              <input
                type="color"
                value={textBox.background_color || '#ffffff'}
                onChange={(e) => handleChange('background_color', e.target.value)}
                className="w-8 h-8 rounded cursor-pointer border border-slate-600"
              />
              <Input
                value={textBox.background_color || ''}
                onChange={(e) => handleChange('background_color', e.target.value || null)}
                className="flex-1 bg-slate-800 border-slate-600 text-white font-mono text-xs h-8"
                placeholder="transparent"
              />
            </div>
          </div>

          {/* Alignment */}
          <div className="space-y-1">
            <Label className="text-slate-400 text-xs">Alignment</Label>
            <div className="flex gap-1">
              {[
                { value: 'left', icon: AlignLeft },
                { value: 'center', icon: AlignCenter },
                { value: 'right', icon: AlignRight },
              ].map(({ value, icon: Icon }) => (
                <Button
                  key={value}
                  variant={textBox.text_align === value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleChange('text_align', value)}
                  className={`flex-1 h-8 ${textBox.text_align === value 
                    ? 'bg-yellow-500 text-slate-900 hover:bg-yellow-600' 
                    : 'border-slate-600 text-slate-300 hover:bg-slate-700'}`}
                >
                  <Icon className="w-4 h-4" />
                </Button>
              ))}
            </div>
          </div>

          {/* Rotation */}
          <div className="space-y-1">
            <Label className="text-slate-400 text-xs flex items-center gap-1">
              <RotateCw className="w-3 h-3" />
              Rotation (°)
            </Label>
            <Input
              type="number"
              value={textBox.rotation || 0}
              onChange={(e) => {
                let val = parseInt(e.target.value) || 0;
                val = ((val % 360) + 360) % 360;
                handleChange('rotation', val);
              }}
              className="bg-slate-800 border-slate-600 text-white text-sm h-8"
              step={15}
            />
          </div>

          {/* Size */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-slate-400 text-xs">Width</Label>
              <Input
                type="number"
                value={textBox.width || 150}
                onChange={(e) => handleChange('width', Math.max(50, parseInt(e.target.value) || 150))}
                className="bg-slate-800 border-slate-600 text-white text-sm h-8"
                min={50}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-400 text-xs">Height</Label>
              <Input
                type="number"
                value={textBox.height || 40}
                onChange={(e) => handleChange('height', Math.max(20, parseInt(e.target.value) || 40))}
                className="bg-slate-800 border-slate-600 text-white text-sm h-8"
                min={20}
              />
            </div>
          </div>

          {/* Delete Button */}
          <Button
            variant="destructive"
            size="sm"
            className="w-full h-8"
            onClick={() => onDelete(textBox.id)}
          >
            <Trash2 className="w-3 h-3 mr-1" />
            Delete
          </Button>
        </div>
      )}
    </div>
  );
};

export default TextBoxFloatingPanel;
